import Box16 from '@carbon/icons-react/es/box/16';
import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router';
import { ProjectId } from 'api/models';
import { Project } from 'api/project';
import { Menu } from 'components/atoms/dropdown/menu';
import { MoreActionsDropdown } from 'components/atoms/more-actions';
import { ToastDescription, toastService } from 'components/molecules/toast';
import { RoutePaths } from 'constant';
import { useDeleteConfirmModal } from 'hooks/use-delete-confirm-modal';
import { useTranslate, useTranslation } from 'hooks/use-translation';
import {
  useDeleteProjectMutation,
  useUnarchiveProjectMutation,
} from 'queries/project';
import { getProjectPlanUrl } from 'utils/urls';

const menu = ({
  onDeleteProject,
  onUnarchiveProject,
}: {
  onDeleteProject(): void;
  onUnarchiveProject(): void;
}) => {
  return (
    <Menu>
      <Menu.Header>
        <FormattedMessage id="more_actions" />
      </Menu.Header>
      <Menu.Item
        onClick={onUnarchiveProject}
        icon={<Box16 />}
        data-test-id="more_actions.unarchive"
      >
        <FormattedMessage id="unarchive" />
      </Menu.Item>
      <Menu.Divider />
      <Menu.ItemDelete onClick={onDeleteProject}>
        <FormattedMessage id="project.delete.cta.title" />
      </Menu.ItemDelete>
    </Menu>
  );
};

export const MoreActionsMenu: FC<{
  project: Project;
}> = ({ project }) => {
  const deleteTitle = useTranslation('project.delete.cta.title');
  const unarchiveTitle = useTranslation('project.action.unarchive.title');
  const deleteButtonText = useTranslation('delete');
  const unarchiveButtonText = useTranslation('unarchive');
  const deleteSuccessMessage = useTranslation(
    'project.action.delete.modal.delete.message',
    { projectName: project.projectName },
  );
  const { projectName } = project;
  const deleteErrorTitle = useTranslation('error_action');
  const deleteErrorMessage = useTranslation('error');
  const deleteModalText = useTranslation('project.action.delete.modal.text');
  const translate = useTranslate();
  const history = useHistory();
  const { mutate: deleteProjMutate } = useDeleteProjectMutation();
  const unarchiveErrorTitle = useTranslation('error_action');
  const unarchiveErrorMessage = useTranslation('error');
  const cta = useTranslation('project.action.unarchive.modal.unarchive.cta');
  const unarchiveSuccessMessage = useTranslation(
    'project.action.unarchive.modal.unarchive.message',
    { projectName },
  );

  const { mutate: unarchiveProjMutate } = useUnarchiveProjectMutation();
  const navigateToProject = () => {
    history.push(getProjectPlanUrl(ProjectId(project.projectId)));

    return history.go(0);
  };
  const handleProjectUnarchive = () => {
    unarchiveProjMutate(
      { projectId: project.projectId },
      {
        onSuccess: () => {
          toastService({
            title: unarchiveTitle,
            message: (
              <ToastDescription
                buttonText={cta}
                message={unarchiveSuccessMessage}
                onClick={navigateToProject}
              />
            ),
          }).success();
        },
        onError: () => {
          toastService({
            title: unarchiveErrorTitle,
            message: unarchiveErrorMessage,
          }).error();
        },
      },
    );
  };

  const unarchiveDeleteConfirm = useDeleteConfirmModal({
    title: unarchiveTitle,
    okText: unarchiveButtonText,
    okButtonProps: { type: 'primary', danger: true },
    onOk: handleProjectUnarchive,
    content: (
      <>
        {translate('project.action.unarchive.modal.footer_confirm_text', {
          b: () => <b>{project.projectName}</b>,
        })}
      </>
    ),
  });
  const handleProjectDelete = () => {
    deleteProjMutate(
      { projectId: project.projectId },
      {
        onSuccess: () => {
          toastService({
            toastContainer: {},
            title: deleteTitle,
            message: deleteSuccessMessage,
          }).success();

          return history.push(RoutePaths.archivedProjects ?? RoutePaths.home);
        },
        onError: () => {
          toastService({
            title: deleteErrorTitle,
            message: deleteErrorMessage,
          }).error();
        },
      },
    );
  };
  const deleteConfirm = useDeleteConfirmModal({
    title: deleteTitle,
    okText: deleteButtonText,
    okButtonProps: { type: 'primary', danger: true },
    onOk: handleProjectDelete,
    content: (
      <>
        {deleteModalText}
        <ul>
          {translate('project.action.delete.modal.points', {
            li: (chunks) => <li>{chunks}</li>,
          })}
        </ul>
        {translate('project.action.delete.modal.footer_confirm_text', {
          b: () => <b>{project.projectName}</b>,
        })}
      </>
    ),
  });

  const onDeleteProject = () => {
    deleteConfirm();
  };

  const onUnarchiveProject = () => {
    unarchiveDeleteConfirm();
  };

  return (
    <>
      <MoreActionsDropdown
        menu={menu({
          onDeleteProject,
          onUnarchiveProject,
        })}
      />
    </>
  );
};
